import { useState } from 'react';

import { Labels } from '@/models/labels';

import { PendingLabel } from '../types';
import { hasName, isSameName } from '../utils/matching';

export const usePendingLabel = (labels: Labels.Details[]) => {
  const [pending, setPending] = useState<PendingLabel[]>([]);
  const [removing, setRemoving] = useState<number[]>([]);
  const [labelToConfirm, setLabelToConfirm] = useState<Labels.Details | null>(
    null
  );
  const [confirmAnchor, setConfirmAnchor] = useState<HTMLElement | null>(null);

  const isAssigned = (name: string) => hasName(labels, name);
  const isPending = (name: string) => hasName(pending, name);
  const isTaken = (name: string) => isAssigned(name) || isPending(name);
  const assignedIds = labels.map((l) => l.id);
  const isUnavailable = (id: number) =>
    assignedIds.includes(id) || pending.some((p) => p.id === id);

  const addPending = (option: PendingLabel) => {
    const name = option.name.trim();
    if (!name || isTaken(name)) return;
    setPending([
      ...pending,
      {
        name,
        id: option.id,
        categoryId: option.categoryId,
        categoryName: option.categoryName,
        needsLink: option.needsLink
      }
    ]);
  };

  const removePending = (name: string) =>
    setPending(pending.filter((p) => !isSameName(p.name, name)));
  const stageRemoval = (id: number) => setRemoving([...removing, id]);
  const undoRemoval = (id: number) =>
    setRemoving(removing.filter((r) => r !== id));

  const requestRemoval = (
    label: Labels.Details,
    isCurrentUser: boolean,
    chip: HTMLElement
  ) => {
    if (isCurrentUser) {
      stageRemoval(label.id);
      return;
    }
    setLabelToConfirm(label);
    setConfirmAnchor(chip);
  };

  const closeConfirm = () => setConfirmAnchor(null);
  const confirmRemoval = () => {
    if (labelToConfirm) stageRemoval(labelToConfirm.id);
    closeConfirm();
  };

  const isPendingConfirmation = (labelId: number): boolean =>
    Boolean(confirmAnchor) && labelToConfirm?.id === labelId;

  return {
    pending,
    removing,
    labelToConfirm,
    confirmAnchor,
    isAssigned,
    isPending,
    isTaken,
    isUnavailable,
    addPending,
    removePending,
    requestRemoval,
    undoRemoval,
    closeConfirm,
    confirmRemoval,
    setPending,
    setRemoving,
    isPendingConfirmation
  };
};
